import { GIButton, Modal } from 'app-components';
import React from 'react';
import { useIntl } from 'react-intl/dist';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { INPUT_TYPES } from '../../../../constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '../../../../constants/inputs/validationTypes';
import { ModalContextType } from '../../../../contexts/modalContext';
import Form from '../../../../lib/wrappers/Form';
import UniversalInput from '../../../../lib/wrappers/UniversalInput';

import { ENROLLMENT_STATUSES } from '../../../../constants/individualPortal/dashboard/dashboardModelTypes';
import { validateTextArea } from '../common/enrollmentUtils';
import { IEnrollment, IEnrollmentOverrideModal } from '../common/types';
import { useFamilyMembers, usePolicy, useReason } from '../hooks/useOverrideEnrollment';
import MembersTable from './MembersTable';
import {
    StyledCharacterCounter,
    StyledInputBoxContainer,
    StyledMemberHeading,
    StyledModalContent,
    StyledNoteSection,
    StyledTextAreaContainer,
} from './ModalStyles';
import { ModalTransactionError, ModalTransactionSuccess } from './ModalSuccessError';
import { MODAL_KEYS } from './modalKeys';

interface IModalProps extends ModalContextType {
    enrollment: IEnrollment;
    overrideEnrollment: IEnrollmentOverrideModal;
}

const OverrideEnrollmentStatusModal = ({
    enrollment,
    closeModal,
    overrideEnrollment: {
        handleSubmit,
        content: { title, body },
        successMessage,
        errorMessage,
        clearState,
    },
}: IModalProps) => {
    const intl = useIntl();
    const { id } = useParams<{ id: string }>();
    const { isSuccess, isError } = useSelector((state: any) => state?.memberEnrollment?.enrollmentRequest);
    const { enrollmentandenrolleedetails } = useSelector((state: { memberEnrollment: any }) => state.memberEnrollment);

    const { familyMembers, handleInputChange } = useFamilyMembers(enrollment);
    const { reason, setReason, maxChars, TEXTAREA_MAX_LENGTH, TEXTAREA_MIN_LENGTH } = useReason();
    const { policy, setPolicy } = usePolicy();

    const handleClose = () => {
        closeModal(MODAL_KEYS.OVERRIDE_ENROLLMENT_STATUS);
        clearState();
    };

    const handleContinue = () => {
        const familyMembersPayload = familyMembers.map(
            ({ memberId, effectiveStartDate, effectiveEndDate, ...rest }) => ({
                ...rest,
                startDate: effectiveStartDate,
                endDate: effectiveEndDate,
            }),
        );

        handleSubmit(
            {
                enrollmentId: enrollment.encEnrollmentId,
                caseNumber: enrollment.ssapCaseNumber,
                familyMembers: familyMembersPayload,
                issuerAssignedPolicyId: policy.issuerAssignedPolicyId,
                overrideCommentText: reason.overrideCommentText,
                houseHoldId: enrollment.houseHoldCaseId,
                coverageYear: enrollmentandenrolleedetails.currentCoverageYear,
            },
            enrollment.enrollmentStatus,
        );
    };

    const onFieldChange = inputValue => {
        const value = validateTextArea(inputValue);
        setReason({ overrideCommentText: value });
    };

    return (
        <Modal id={MODAL_KEYS.OVERRIDE_ENROLLMENT_STATUS} data-testid="override-enrollment-status-modal">
            <Modal.Header
                id="overrideEnrollmentStatusModalTitle"
                titleId={title}
                onHide={handleClose}
                data-testid="modal-header"
            />
            <Modal.Body data-testid="modal-body">
                <ModalTransactionSuccess message={successMessage} isTrue={isSuccess} />
                <ModalTransactionError message={errorMessage} isTrue={isError} />

                <ConditionalRendering isTrue={!isSuccess && !isError}>
                    <StyledModalContent data-testid="modal-content">{body}</StyledModalContent>

                    <Form
                        formName="override-enrollment-status-form"
                        data-testid="override-enrollment-status-modal-form"
                    >
                        <StyledTextAreaContainer data-testid="textarea-container">
                            <UniversalInput
                                label={{
                                    text: intl.formatMessage({ id: 'member.enrollment.modal.textarea.label' }),
                                    labelClasses: ['spl__width--01'],
                                }}
                                inputData={{
                                    isInputRequired: true,
                                    type: INPUT_TYPES.TEXTAREA,
                                    name: 'overrideCommentText',
                                    value: reason.overrideCommentText,
                                    liClass: ['text-left'],
                                    currentData: reason,
                                    placeholder: intl.formatMessage({
                                        id: 'member.enrollment.modal.textarea.placeholder',
                                    }),
                                    rows: 8,
                                    validationType: VALIDATION_TYPES.LENGTH,
                                    valuesToMatch: [TEXTAREA_MIN_LENGTH, TEXTAREA_MAX_LENGTH],
                                }}
                                errorClasses={['margin-l-0']}
                                inputActions={{
                                    onFieldChange: ({ overrideCommentText }) => {
                                        onFieldChange(overrideCommentText);
                                    },
                                }}
                                data-testid="textarea-input"
                            />
                        </StyledTextAreaContainer>
                        <StyledCharacterCounter data-testid="character-counter">
                            {maxChars} {intl.formatMessage({ id: 'member.enrollment.modal.textCharLeft' })}
                        </StyledCharacterCounter>

                        <StyledNoteSection data-testid="note-section">
                            {intl.formatMessage({ id: 'member.enrollment.modal.overrideEnrollmentStatus.note' })}
                        </StyledNoteSection>

                        <StyledInputBoxContainer data-testid="input-box-container">
                            <UniversalInput
                                label={{
                                    text: intl.formatMessage({
                                        id: 'member.enrollment.modal.overrideEnrollmentStatus.issuerPolicyID',
                                    }),
                                    labelClasses: ['spl__width--01'],
                                }}
                                inputData={{
                                    type: INPUT_TYPES.REGULAR_INPUT,
                                    name: 'issuerAssignedPolicyId',
                                    value: policy.issuerAssignedPolicyId,
                                    liClass: ['text-left'],
                                    currentData: policy,
                                }}
                                errorClasses={['margin-l-0']}
                                inputActions={{
                                    onFieldChange: ({ issuerAssignedPolicyId }) => {
                                        setPolicy(prev => ({ ...prev, issuerAssignedPolicyId }));
                                    },
                                }}
                                data-testid="issuer-policy-id-input"
                            />
                        </StyledInputBoxContainer>

                        <StyledMemberHeading data-testid="member-heading">
                            {intl.formatMessage({
                                id: 'member.enrollment.modal.overrideEnrollmentStatus.coveredMembers',
                            })}
                        </StyledMemberHeading>

                        <MembersTable data={familyMembers} handleInputChange={handleInputChange} />
                    </Form>
                </ConditionalRendering>
            </Modal.Body>
            <Modal.Controls data-testid="modal-controls">
                <ConditionalRendering isTrue={isSuccess || isError}>
                    <GIButton
                        id="button"
                        onClickHandler={handleClose}
                        variant="secondary"
                        labelId="button.ok"
                        data-testid="ok-button"
                    />
                </ConditionalRendering>

                <ConditionalRendering isTrue={!isSuccess && !isError}>
                    <>
                        <GIButton
                            isDisabled={!reason.overrideCommentText.length}
                            id="button"
                            onClickHandler={handleContinue}
                            variant="primary"
                            labelId={
                                enrollment.enrollmentStatus === ENROLLMENT_STATUSES.PEND
                                    ? 'member.enrollment.modal.overrideEnrollmentStatus.btnPending'
                                    : 'member.enrollment.modal.overrideEnrollmentStatus.btnConfirmed'
                            }
                            data-testid="confirm-button"
                        />
                        <GIButton
                            id="button"
                            onClickHandler={handleClose}
                            variant="secondary"
                            labelId="button.cancel"
                            data-testid="cancel-button"
                        />
                    </>
                </ConditionalRendering>
            </Modal.Controls>
        </Modal>
    );
};

export default OverrideEnrollmentStatusModal;
